import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { SlideCard } from "./SlideCard";
import { SlidesSidebar } from "./SlidesSidebar";
import { PdfPreview } from "./PdfPreview";
import { usePresentation } from "@/zapi-frontend/shared/hooks/usePresentation";
import { RefreshOutlineButton } from "./RefreshOutlineButton";

import { PresentationInstructionsModal } from "./PresentationInstructionsModal";
import { formatGeneratedTime } from "../lib/formatGeneratedTime";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { PresentationChat } from "./PresentationChat";
import { LeoChat } from "@/zapi-frontend/widgets/LeoChat";
import { useLeoChatStore } from "@/zapi-frontend/stores/leoChatStore";
import { usePresentationChatStore } from "../hooks/usePresentationChat";
import { ChatPanelLoading, DockedChatIsland } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { useChatUnreadIndicator } from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useParams, useSearchParams } from "react-router-dom";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { TViewMode } from "../model/types";
import { PresentationBuilder, type TOutlineState, type TSlidesAction } from "./PresentationBuilder";

// Above the 36px the formatting toolbar adds to the current slide.
const SELECTION_SLACK_PX = 48;

export function PresentationPage({ chatSlot }: { chatSlot: HTMLElement | null }) {
  const { t } = useTranslation("projectHome");
  const {
    slides,
    isLoading,
    isSlidesGenerating,
    isSlidesRegenerating,
    isGammaGenerating,
    gammaPdfUrl,
    outlineWasRefreshed,
    hasSlides,
    presentation,
    presentationExists,
    outlineGeneratedAt,
    gammaGeneratedAt,
    allowRegeneration,
    updateSlide,
    deleteSlide,
    addSlide,
    reorderSlides,
    generateSlides,
    regenerateSlides,
    generateGammaPreview,
    isSaving
  } = usePresentation();
  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { isLabs, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("view") as TViewMode;
  const { id: projectId = "" } = useParams();
  // A Claude-generated project (no template) has Leo as its only chat, handed the outline as
  // work/slides.json, and its outline and deck are kept current by the generation and by Leo, so
  // there is nothing to refresh by hand; Create Slides is left only for a project with no deck.
  // A templated project keeps the legacy presentation chat and the Gamma flow.
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";
  const isLeoActive = isTemplateLess;
  const isDeckFollowingOutline = isTemplateLess;
  const presentationAssistantCount = usePresentationChatStore(
    (s) => (s.chatHistory[projectId] ?? []).filter((m) => m.role !== "user" && !m.id.startsWith("notification-")).length
  );
  const leoAssistantCount = useLeoChatStore(
    (s) => (s.historyByProject[projectId] ?? []).filter((m) => m.role === "assistant").length
  );
  const assistantMessageCount = isLeoActive ? leoAssistantCount : presentationAssistantCount;
  const isChatHistoryEmpty = usePresentationChatStore((s) => (s.chatHistory[projectId] ?? []).length === 0);
  // The chat's result replaces the whole outline, so edits made while it runs would be lost.
  const isPresentationChatRunning = usePresentationChatStore((s) => !!s.loadingStates[projectId]?.loadingMessageId);
  const isLeoEditRunning = useLeoChatStore((s) => !!s.activeTaskByProject[projectId]?.canEdit);
  const isChatRunning = isPresentationChatRunning || isLeoEditRunning;
  const {
    hasUnread: hasUnreadChat,
    markSeen: markChatSeen,
    markUnread: markChatUnread
  } = useChatUnreadIndicator(assistantMessageCount, {
    firstVisitKey: projectId ? `slides:${projectId}` : undefined,
    conversationKey: isLeoActive ? `leo:${projectId}` : projectId
  });

  // `PresentationChatMessageList` renders the "Your outline is ready!" bubble itself
  // rather than storing it, so it never reaches the message count the dot is derived from.
  const showsOutlineReadyMessage = isChatHistoryEmpty && hasSlides && !isSlidesGenerating;
  // Whether that bubble is up is unknowable until the project reports whether a
  // presentation exists and, if one does, it has been fetched. Read any earlier, an
  // existing outline looks absent and then arrives, which is indistinguishable from
  // one the user just generated. A presentation still generating needs no fetch — the
  // bubble is replaced by the generation steps until it finishes.
  const isOutlineResolved =
    selectedProject !== null &&
    !isFetchProjectLoading &&
    (!presentationExists || presentation !== null || isSlidesGenerating);

  const slidesContainerRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const [outlineSlideIndex, setOutlineSlideIndex] = useState(0);
  const [previewPageIndex, setPreviewPageIndex] = useState(1);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);

  const isPageLoading = isLoading || isFetchProjectLoading;
  const isDisabledGeneratingSlides = isSlidesGenerating || !hasSlides || isSaving;
  const showRefreshSlides = (outlineWasRefreshed || allowRegeneration) && gammaGeneratedAt != null;
  const isOutlineRefreshing = isSlidesGenerating && hasSlides;

  const scrollSettleRef = useRef<ReturnType<typeof setTimeout>>();
  const holdUntilRef = useRef(0);
  const heldSlideIdRef = useRef("");

  function isOnScreen(element: Element | null) {
    const container = slidesContainerRef.current;
    if (!container || !element) return false;
    const list = container.getBoundingClientRect();
    const box = element.getBoundingClientRect();
    return box.bottom > list.top && box.top < list.bottom;
  }

  function pickSlideUnderReadingLine() {
    const container = slidesContainerRef.current;
    if (!container) return;
    const cards = Array.from(container.querySelectorAll('article[id^="slide-"]'));
    if (cards.length === 0) return;
    const current = cards.findIndex((card) => card.hasAttribute("aria-current"));
    const lastIndex = cards.length - 1;
    const distanceToEnd = container.scrollHeight - container.clientHeight - container.scrollTop;
    if (distanceToEnd <= (current === lastIndex ? SELECTION_SLACK_PX : 1)) {
      setOutlineSlideIndex(lastIndex);
      return;
    }
    const list = container.getBoundingClientRect();
    const readingLine = list.top + Math.min(list.height / 3, 120);
    const isUnderLine = (card: Element | undefined, slack: number) => {
      if (!card) return false;
      const box = card.getBoundingClientRect();
      return box.top - slack <= readingLine && box.bottom + slack >= readingLine;
    };
    if (isUnderLine(cards[current], SELECTION_SLACK_PX)) return;
    const index = cards.findIndex((card) => isUnderLine(card, 0));
    if (index !== -1) setOutlineSlideIndex(index);
  }

  function holdSelectionWhileScrolling(slideId: string, extend = false) {
    isProgrammaticScrollRef.current = true;
    heldSlideIdRef.current = slideId;
    if (!extend) holdUntilRef.current = Date.now() + 1000;
    clearTimeout(scrollSettleRef.current);
    scrollSettleRef.current = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
      if (!isOnScreen(document.getElementById(`slide-${slideId}`))) pickSlideUnderReadingLine();
    }, 150);
  }

  function selectSlide(index: number) {
    const slide = slides[index];
    if (!slide) return;
    holdSelectionWhileScrolling(slide.id);
    setOutlineSlideIndex(index);
    document.getElementById(`slide-${slide.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleSlidesScroll() {
    if (isProgrammaticScrollRef.current) {
      if (Date.now() < holdUntilRef.current) holdSelectionWhileScrolling(heldSlideIdRef.current, true);
      return;
    }
    const current = slides[outlineSlideIndex] && document.getElementById(`slide-${slides[outlineSlideIndex].id}`);
    if (current?.contains(document.activeElement) && isOnScreen(current)) return;
    pickSlideUnderReadingLine();
  }

  function handleAddSlide() {
    const newSlideId = addSlide(outlineSlideIndex);
    const newIndex = outlineSlideIndex + 1;

    setTimeout(() => {
      holdSelectionWhileScrolling(newSlideId);
      setOutlineSlideIndex(newIndex);
      document.getElementById(`slide-${newSlideId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  function switchToOutline() {
    setSearchParams({ tab: "presentation", view: "outline" }, { replace: true });
    setTimeout(() => {
      if (slides[outlineSlideIndex]) selectSlide(outlineSlideIndex);
    }, 50);
  }

  const switchToPreview = () => setSearchParams({ tab: "presentation", view: "preview" }, { replace: true });
  const handleCreateSlides = () => generateGammaPreview();
  const handleGenerateSlides = () => generateSlides();

  useEffect(() => {
    if (!viewMode) setSearchParams({ tab: "presentation", view: "outline" }, { replace: true });
  }, [viewMode, setSearchParams]);

  const seenOutlineReadyRef = useRef<{ projectId: string; shown: boolean } | null>(null);
  useEffect(() => {
    if (!isOutlineResolved) return;
    const seen = seenOutlineReadyRef.current;
    seenOutlineReadyRef.current = { projectId, shown: showsOutlineReadyMessage };
    if (!seen || seen.projectId !== projectId) return;
    if (!seen.shown && showsOutlineReadyMessage) markChatUnread();
  }, [isOutlineResolved, showsOutlineReadyMessage, projectId, markChatUnread]);

  const outlineState: TOutlineState =
    isSlidesGenerating || isSlidesRegenerating
      ? "generating"
      : isPageLoading
      ? "loading"
      : hasSlides
      ? "ready"
      : "empty";
  const canPreview =
    hasEdit &&
    !!gammaPdfUrl &&
    (isDeckFollowingOutline || (!allowRegeneration && !isGammaGenerating && !isSlidesRegenerating));
  const slidesAction: TSlidesAction | null =
    !hasEdit || (gammaPdfUrl && (isDeckFollowingOutline || (!allowRegeneration && !isGammaGenerating)))
      ? null
      : {
          refresh: showRefreshSlides,
          busy: isGammaGenerating,
          disabled: isDisabledGeneratingSlides || isGammaGenerating,
          onClick: handleCreateSlides
        };

  return (
    <>
      <PresentationBuilder
        view={viewMode === "preview" ? "preview" : "outline"}
        onViewChange={(next) => (next === "preview" ? switchToPreview() : switchToOutline())}
        hasDeck={canPreview}
        deckUpdating={hasEdit && isDeckFollowingOutline && isGammaGenerating && !!gammaPdfUrl}
        outlineState={outlineState}
        outlineRefreshing={isOutlineRefreshing || isSlidesRegenerating}
        outlineMeta={formatGeneratedTime(outlineGeneratedAt, outlineWasRefreshed ? "refreshed" : "created", t)}
        canEdit={hasEdit}
        showInstructions={!!isLabs}
        onOpenInstructions={() => setIsInstructionsOpen(true)}
        onCreateOutline={handleGenerateSlides}
        refreshOutline={
          hasEdit && !isDeckFollowingOutline ? (
            <RefreshOutlineButton
              outlineGeneratedAt={outlineGeneratedAt}
              hasSlides={hasSlides}
              onRefresh={regenerateSlides}
              isRefreshing={isSlidesRegenerating || isOutlineRefreshing}
              disabled={isGammaGenerating || isSaving || isSlidesGenerating}
            />
          ) : null
        }
        slidesAction={slidesAction}
        slideList={
          <SlidesSidebar
            slides={slides}
            selectedIndex={outlineSlideIndex}
            isGenerating={isSlidesGenerating || isPageLoading}
            isReadOnly={isChatRunning}
            onSelectSlide={selectSlide}
            onDeleteSlide={deleteSlide}
            onAddSlide={handleAddSlide}
            onReorderSlides={reorderSlides}
          />
        }
        slideCards={slides.map((slide, index) => (
          <SlideCard
            key={slide.id}
            slide={slide}
            index={index}
            selectedIndex={outlineSlideIndex}
            onUpdate={updateSlide}
            onDelete={deleteSlide}
            onSelect={selectSlide}
            isReadOnly={isChatRunning}
          />
        ))}
        slidesRef={slidesContainerRef}
        onSlidesScroll={handleSlidesScroll}
        preview={
          (!isGammaGenerating || (isDeckFollowingOutline && gammaPdfUrl)) && (
            <PdfPreview
              pdfUrl={gammaPdfUrl}
              initialPage={previewPageIndex}
              onPageChange={setPreviewPageIndex}
              isPageLoading={isPageLoading}
            />
          )
        }
      />
      {chatSlot &&
        createPortal(
          <DockedChatIsland appearance="design-system" hasUnread={hasUnreadChat} onOpenChange={markChatSeen}>
            {String(selectedProject?.id) !== projectId ? (
              <ChatPanelLoading />
            ) : isLeoActive ? (
              <LeoChat projectId={projectId} slideIndex={hasSlides ? outlineSlideIndex : undefined} />
            ) : (
              <PresentationChat selectedSlideIndex={outlineSlideIndex} />
            )}
          </DockedChatIsland>,
          chatSlot
        )}
      <PresentationInstructionsModal open={isInstructionsOpen} onClose={() => setIsInstructionsOpen(false)} />
    </>
  );
}
